import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ARFD_CLASSES } from '../data/arfdObjectives';
import CustomSelect from './CustomSelect';

const WFD_CLASSES = [
  { value: 'H', label: 'H - Home Station' },
  { value: 'I', label: 'I - Indoor Station' },
  { value: 'O', label: 'O - Outdoor Station' },
  { value: 'M', label: 'M - Mobile Station' }
];

function latLonToGrid(lat, lon) {
  // Maidenhead locator to 6-character precision
  const adjLon = lon + 180;
  const adjLat = lat + 90;
  const fieldLon = Math.floor(adjLon / 20);
  const fieldLat = Math.floor(adjLat / 10);
  const squareLon = Math.floor((adjLon % 20) / 2);
  const squareLat = Math.floor(adjLat % 10);
  const subLon = Math.floor(((adjLon % 20) % 2) / (2 / 24));
  const subLat = Math.floor((adjLat % 1) / (1 / 24));
  return (
    String.fromCharCode(65 + fieldLon) +
    String.fromCharCode(65 + fieldLat) +
    squareLon +
    squareLat +
    String.fromCharCode(97 + subLon) +
    String.fromCharCode(97 + subLat)
  );
}

function EventSettings({ token, apiUrl, onSettingsChange }) {
  const [settings, setSettings] = useState({
    club_callsign: '',
    club_grid: '',
    club_section: '',
    event_class: 'O',
    event_category: '1',
    event_type: 'WFD'
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [geolocating, setGeolocating] = useState(false);
  const [geoError, setGeoError] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
      setLoaded(true);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setLoaded(true);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // When switching event type, reset class to a valid default for that type.
  const handleEventTypeChange = (newType) => {
    const defaultClass = newType === 'ARFD' ? 'A' : 'O';
    setSettings(prev => ({
      ...prev,
      event_type: newType,
      event_class: defaultClass,
      // ARFD classes B-F are single-transmitter only; reset category if needed
      event_category: newType === 'ARFD'
        ? (isMultiTxClass(defaultClass) ? prev.event_category : '1')
        : prev.event_category
    }));
  };

  // When switching ARFD class, lock transmitter count to 1 for single-tx classes.
  const handleClassChange = (newClass) => {
    setSettings(prev => ({
      ...prev,
      event_class: newClass,
      event_category: (settings.event_type === 'ARFD' && !isMultiTxClass(newClass))
        ? '1'
        : prev.event_category
    }));
  };

  const isMultiTxClass = (cls) => {
    const arfdClass = ARFD_CLASSES.find(c => c.value === cls);
    return arfdClass ? arfdClass.multiTx : true;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await axios.put(`${apiUrl}/api/settings`, { settings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Settings saved successfully!');
      if (onSettingsChange) onSettingsChange(settings);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Error saving settings');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by this browser.');
      return;
    }
    setGeolocating(true);
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const grid = latLonToGrid(pos.coords.latitude, pos.coords.longitude);
        handleChange('club_grid', grid);
        setGeolocating(false);
      },
      (err) => {
        if (err.code === 1) {
          setGeoError('Location denied — check browser and macOS System Settings → Privacy → Location Services.');
        } else if (err.code === 3) {
          setGeoError('Location timed out.');
        } else {
          setGeoError(`Location unavailable: ${err.message}`);
        }
        setGeolocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const validateGrid = (grid) => {
    const gridRegex = /^[A-R]{2}[0-9]{2}([a-x]{2})?$/i;
    return grid === '' || gridRegex.test(grid);
  };

  const isArfd = settings.event_type === 'ARFD';
  const classes = isArfd ? ARFD_CLASSES : WFD_CLASSES;
  const multiTxAllowed = !isArfd || isMultiTxClass(settings.event_class);

  // Build the exchange preview string for the current settings.
  // WFD:  <callsign> <category><class> <section>  e.g. "W1AW 2O NNJ"
  // ARFD: <callsign> <category><class> <section>  e.g. "W1AW 2A NNJ"
  //       (structurally identical; class letters differ)
  const exchangePreview = loaded
    ? `${settings.club_callsign || '(callsign)'} ${settings.event_category}${settings.event_class} ${settings.club_section || '(section)'}`
    : null;

  return (
    <div className="settings-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 className="settings-title" style={{ margin: 0 }}>Event Settings</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {geoError && <small className="error-text">{geoError}</small>}
          <button
            type="button"
            className="btn btn-secondary btn-small"
            onClick={handleGeolocate}
            disabled={geolocating}
            title="Auto-fill grid square from your current location"
          >
            {geolocating ? 'Locating...' : 'Use My Location'}
          </button>
        </div>
      </div>

      {/* Event type selector — top of form, affects everything below */}
      <div className="settings-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Contest / Event Type</label>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal', cursor: 'pointer' }}>
              <input
                type="radio"
                name="event_type"
                value="WFD"
                checked={settings.event_type === 'WFD'}
                onChange={() => handleEventTypeChange('WFD')}
              />
              Winter Field Day (WFD)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal', cursor: 'pointer' }}>
              <input
                type="radio"
                name="event_type"
                value="ARFD"
                checked={settings.event_type === 'ARFD'}
                onChange={() => handleEventTypeChange('ARFD')}
              />
              ARRL Field Day (ARFD)
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Club Callsign</label>
          <input
            type="text"
            value={settings.club_callsign}
            onChange={(e) => handleChange('club_callsign', e.target.value.toUpperCase())}
            placeholder="W1AW"
            maxLength="10"
          />
        </div>

        <div className="form-group">
          <label>Club QTH Grid Square</label>
          <input
            type="text"
            value={settings.club_grid}
            onChange={(e) => handleChange('club_grid', e.target.value.toUpperCase())}
            placeholder="FN31pr"
            maxLength="6"
            className={!validateGrid(settings.club_grid) ? 'invalid' : ''}
          />
          {!validateGrid(settings.club_grid) && (
            <small className="error-text">Invalid grid format (e.g., FN31 or FN31pr)</small>
          )}
        </div>

        <div className="form-group">
          <label>ARRL Section</label>
          <input
            type="text"
            value={settings.club_section}
            onChange={(e) => handleChange('club_section', e.target.value.toUpperCase())}
            placeholder="NNJ"
            maxLength="4"
          />
        </div>

        <div className="form-group">
          <label>Event Class</label>
          <CustomSelect
            value={settings.event_class}
            onChange={handleClassChange}
            options={classes.map(c => ({ value: c.value, label: c.label, sublabel: c.sublabel }))}
          />
        </div>

        <div className="form-group">
          <label># of Transmitters</label>
          <CustomSelect
            value={String(settings.event_category)}
            onChange={(val) => handleChange('event_category', val)}
            options={[1,2,3,4,5,6,7,8,9,10].map(n => ({ value: String(n), label: String(n) }))}
            disabled={!multiTxAllowed}
          />
          {!multiTxAllowed && (
            <small className="info-text">Class {settings.event_class} is limited to 1 transmitter</small>
          )}
        </div>
      </div>

      <div className="exchange-preview">
        <strong>Your Exchange:</strong>{' '}
        {exchangePreview ?? <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Loading...</span>}
        {loaded && settings.club_grid && <span className="grid-info"> (Grid: {settings.club_grid})</span>}
        <span className="exchange-mode-badge" style={{
          marginLeft: '0.75rem',
          fontSize: '0.75rem',
          padding: '0.1rem 0.5rem',
          borderRadius: '3px',
          background: isArfd ? '#1a4a8a' : '#1a5c3a',
          color: '#fff'
        }}>
          {isArfd ? 'ARRL FD' : 'WFD'}
        </span>
      </div>

      <div className="settings-actions">
        <button
          className="btn"
          onClick={handleSave}
          disabled={saving || !validateGrid(settings.club_grid)}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {message && (
          <span className={message.includes('Error') ? 'error-text' : 'success-text'}>
            {message}
          </span>
        )}
      </div>
    </div>
  );
}

export default EventSettings;
